import React from 'react';
import { Route, Switch, Redirect, useLocation } from 'react-router-dom';
import { Preload } from '@qover/atoms';
import SetStartDateNextDay from '@qover/utils/src/SetStartDateNextDay';
import { PaymentPage } from '@qover/pages';
import {
  TripRedirection,
  TripDurationRedirection,
  LandingRedirection,
  QuoteRedirection,
} from './components/TrRedirection';
import { TrShortTripLocation } from './pages/shortTripLocation/trShortTripLocation.loadable';
import { TrConfirmation } from './pages/confirmation/trConfirmation.loadable';
import { TrCreateDraft } from './pages/createDraft/trCreateDraft.loadable';
import { TrFinalization } from './pages/finalization/trFinalization.loadable';
import { TrInsured } from './pages/insured/trInsured.loadable';
import { TrMaxTripDuration } from './pages/maxTripDuration/trMaxTripDuration.loadable';
import { TrOptions } from './pages/options/trOptions.loadable';
import { TrPeriod } from './pages/period/trPeriod.loadable';
import {
  TrQuote,
  TrQuoteMultiTrip,
  TrQuoteExpat,
} from './pages/quote/trQuote.loadable';
import { TrSummary } from './pages/summary/trSummary.loadable';
import { Draft } from './domain/Draft';
import type { DraftProps } from './domain/Draft';
import { Path } from './domain/Step';
import PriceInfo from './domain/PriceInfo';
import { PaymentRequest } from './domain/PaymentRequest';
import { PaymentRythm } from './domain/PaymentRythm';
import {
  TrDiscontinuanceModal,
  TrDiscontinuanceModalWithTripCheck,
} from './components/TrDiscontinuanceModal';
import { Wizard } from '@qover/hooks/src/useDraft';

type RoutesProps = {
  currentStep?: Path;
  draft: Draft;
  form: DraftProps;
  handleSubmit: any;
  linkTo: any;
  match: any;
  prices: PriceInfo;
  risk: any;
  terms: any;
  wizard: Wizard<Draft>;
};

export const Routes: React.FC<RoutesProps> = props => {
  const {
    currentStep,
    draft,
    handleSubmit,
    linkTo,
    match,
    prices,
    terms,
  } = props;

  const location = useLocation();
  const price = draft?.getTotalPrice() ?? 0;
  const secondaryPrice = draft?.getTotalPrice(PaymentRythm.YEARLY) ?? 0;

  const onEmailModalSubmit = (form: { email: string }) =>
    props.wizard.updateEmail('save', form.email);

  return (
    <Switch>
      <Route
        exact
        path={`${match.path}/create-draft`}
        render={() => (
          <TrCreateDraft
            handleCreateOnMount={props.wizard.handleCreateOnMount}
            match={match}
          />
        )}
      />
      <Route
        path={`${match.path}/trip-duration`}
        render={() => (
          <>
            <Preload
              components={[TrQuote, TrQuoteMultiTrip, TrShortTripLocation]}
            />
            <TrPeriod onSubmit={handleSubmit} backTo="#" />
          </>
        )}
      />
      <Route
        path={`${match.path}/max-trip-duration`}
        render={() => (
          <>
            <TrDiscontinuanceModal linkTo={linkTo('trip-duration')} />
            <Preload component={TrQuote} />
            <TrMaxTripDuration
              onSubmit={handleSubmit}
              backTo={linkTo('needs')}
            />
          </>
        )}
      />
      <Route
        path={`${match.path}/short-trip-location`}
        render={() => (
          <>
            <TripDurationRedirection match={match}>
              <TrDiscontinuanceModalWithTripCheck
                linkTo={linkTo('trip-duration')}
              />
              <Preload component={TrQuote} />
              <TrShortTripLocation
                onSubmit={handleSubmit}
                backTo={linkTo('trip-duration')}
              />
            </TripDurationRedirection>
          </>
        )}
      />
      <Route
        path={`${match.path}/quote-multi-trip`}
        render={() => (
          <>
            <TrDiscontinuanceModal linkTo={linkTo('trip-duration')} />
            <TripDurationRedirection match={match}>
              <TripRedirection match={match}>
                <Preload component={TrOptions} />
                <TrQuoteMultiTrip
                  backTo={linkTo('needs')}
                  computedFamilySituation={Draft.getFamilySituation(props.risk)}
                  form={props.form}
                  linkTo={linkTo}
                  onEmailModalSubmit={onEmailModalSubmit}
                  onSubmit={handleSubmit}
                  prices={prices}
                  tripType={draft?.terms?.tripType}
                />
              </TripRedirection>
            </TripDurationRedirection>
          </>
        )}
      />
      <Route
        path={`${match.path}/quote-expat`}
        render={() => (
          <>
            <TrDiscontinuanceModal linkTo={linkTo('trip-duration')} />
            <TripRedirection match={match}>
              <Preload component={TrOptions} />
              <TrQuoteExpat
                backTo={linkTo('#')}
                computedFamilySituation={Draft.getFamilySituation(props.risk)}
                form={props.form}
                onEmailModalSubmit={onEmailModalSubmit}
                onSubmit={handleSubmit}
                prices={prices}
              />
            </TripRedirection>
          </>
        )}
      />
      <Route
        path={`${match.path}/quote`}
        render={p => (
          <TripDurationRedirection match={match}>
            <TripRedirection match={match}>
              <TrDiscontinuanceModalWithTripCheck
                linkTo={linkTo('trip-duration')}
              />
              <Preload component={TrOptions} />
              <TrQuote
                {...p}
                backTo={linkTo('trip-duration')}
                computedFamilySituation={Draft.getFamilySituation(props.risk)}
                computedVariant={draft?.getVariant()}
                draft={draft}
                form={props.form}
                geographicalArea={draft?.terms?.geographicalArea}
                onEmailModalSubmit={onEmailModalSubmit}
                linkTo={linkTo}
                onSubmit={handleSubmit}
                prices={prices}
                rootUrl={match.path}
                tripDuration={draft?.terms?.tripDuration}
                tripType={draft?.terms?.tripType}
                variant={draft?.terms?.variant}
              />
            </TripRedirection>
          </TripDurationRedirection>
        )}
      />
      <Route
        path={`${match.path}/options`}
        render={() => (
          <TripRedirection match={match}>
            <TrDiscontinuanceModalWithTripCheck
              linkTo={linkTo('trip-duration')}
            />
            <QuoteRedirection match={match}>
              <Preload component={TrInsured} />
              <TrOptions
                backTo={linkTo}
                buttonTitle={currentStep?.buttonTitle}
                draft={props.draft}
                form={props.form}
                onEmailModalSubmit={onEmailModalSubmit}
                onSubmit={handleSubmit}
                optionsPrice={prices?.getOptionsFromDraft(props.draft)}
                prices={prices}
                rootUrl={match.path}
              />
            </QuoteRedirection>
          </TripRedirection>
        )}
      />
      <Route
        exact
        path={`${match.path}/insurers`}
        render={() => (
          <QuoteRedirection match={match}>
            <TrDiscontinuanceModalWithTripCheck
              linkTo={linkTo('trip-duration')}
            />
            <TripRedirection match={match}>
              <Preload component={TrFinalization} />
              <TrInsured
                buttonTitle={currentStep?.buttonTitle}
                backTo={linkTo('options')}
                draft={props.draft}
                modifyTo={linkTo('quote')}
                onSubmit={handleSubmit}
                onEmailModalSubmit={onEmailModalSubmit}
                price={price}
                secondaryPrice={secondaryPrice}
              />
            </TripRedirection>
          </QuoteRedirection>
        )}
      />
      <Route
        exact
        path={`${match.path}/finalization`}
        render={() => (
          <QuoteRedirection match={match}>
            <TripRedirection match={match}>
              <TrDiscontinuanceModalWithTripCheck
                linkTo={linkTo('trip-duration')}
              />
              <Preload component={TrSummary} />
              <SetStartDateNextDay />
              <TrFinalization
                buttonTitle={currentStep?.buttonTitle}
                backTo={linkTo('insurers')}
                draft={props.draft}
                onSubmit={handleSubmit}
                onEmailModalSubmit={onEmailModalSubmit}
                price={price}
                secondaryPrice={secondaryPrice}
              />
            </TripRedirection>
          </QuoteRedirection>
        )}
      />
      <Route
        path={`${match.path}/summary`}
        render={p => (
          <QuoteRedirection match={match}>
            <TripRedirection match={match}>
              <TrDiscontinuanceModalWithTripCheck
                linkTo={linkTo('trip-duration')}
              />
              <Preload component={PaymentPage} />
              <SetStartDateNextDay />
              <TrSummary
                {...p}
                draft={draft}
                linkTo={linkTo}
                onSubmit={handleSubmit}
                prices={prices}
                terms={terms}
                wizard={props.wizard}
              />
            </TripRedirection>
          </QuoteRedirection>
        )}
      />
      <Route
        path={`${match.path}/payment/:type(card|bancontact|direct-debit|sofort|ideal|giropay)?`}
        render={() => (
          <QuoteRedirection match={match}>
            <TrDiscontinuanceModalWithTripCheck
              linkTo={linkTo('trip-duration')}
            />
            <PaymentPage
              backTo={linkTo('summary')}
              draft={props.draft}
              match={match}
              paymentRequest={PaymentRequest}
            />
          </QuoteRedirection>
        )}
      />
      <Route
        exact
        path={`${match.path}/confirmation`}
        render={() => (
          <LandingRedirection match={match}>
            <TrConfirmation />
          </LandingRedirection>
        )}
      />
      <Redirect
        from={match.path}
        push={false}
        to={`${match.path}/trip-duration${location.search}`}
      />
    </Switch>
  );
};
